import { useEffect, useRef, useState } from 'react';

interface TradingViewMarketOverviewProps {
  height?: number;
  symbols?: Array<{ s: string; d?: string }>;
}

export const TradingViewMarketOverview = ({ 
  height = 400,
  symbols = [
    { s: "BINANCE:BTCUSDT", d: "Bitcoin" },
    { s: "BINANCE:ETHUSDT", d: "Ethereum" },
    { s: "BINANCE:BNBUSDT", d: "BNB" },
    { s: "BINANCE:SOLUSDT", d: "Solana" },
    { s: "BINANCE:ADAUSDT", d: "Cardano" },
    { s: "BINANCE:XRPUSDT", d: "Ripple" }
  ]
}: TradingViewMarketOverviewProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isReady, setIsReady] = useState(false);
  const widgetId = useRef(`market-overview-${Date.now()}`);

  // Wait for DOM to be fully ready
  useEffect(() => {
    const timer = requestAnimationFrame(() => {
      setIsReady(true);
    });
    return () => cancelAnimationFrame(timer);
  }, []);

  useEffect(() => {
    if (!isReady || !containerRef.current) return;

    const container = containerRef.current;
    
    // Clear any existing content safely
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }

    // Create the widget container that TradingView expects
    const widgetContainer = document.createElement('div');
    widgetContainer.className = 'tradingview-widget-container__widget';
    widgetContainer.id = widgetId.current;
    container.appendChild(widgetContainer);

    // Create copyright div that TradingView requires
    const copyrightDiv = document.createElement('div');
    copyrightDiv.className = 'tradingview-widget-copyright';
    container.appendChild(copyrightDiv);

    // Load TradingView script after container is ready
    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-market-overview.js';
    script.async = true;
    script.type = 'text/javascript';
    script.innerHTML = JSON.stringify({
      colorTheme: 'dark',
      dateRange: '1D',
      showChart: true,
      locale: 'en',
      width: '100%',
      height: height.toString(),
      largeChartUrl: '',
      isTransparent: false,
      showSymbolLogo: true,
      showFloatingTooltip: false,
      tabs: [
        {
          title: 'Top Cryptos',
          symbols: symbols
        }
      ]
    });

    // Add script after a small delay to ensure DOM is ready
    const scriptTimer = setTimeout(() => {
      if (container.isConnected) {
        container.appendChild(script);
      }
    }, 100);

    return () => {
      clearTimeout(scriptTimer);
      // Clean up safely
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
    };
  }, [height, symbols, isReady]);

  return (
    <div className="w-full rounded-lg overflow-hidden border border-border/50">
      <div 
        ref={containerRef} 
        className="tradingview-widget-container"
        style={{ minHeight: `${height}px` }}
      />
    </div>
  );
};
